import React, { useState } from 'react';
import {
    Award,
    Target,
    CheckCircle,
    XCircle,
    TrendingUp,
    User,
    Edit
} from 'lucide-react';

const PerformanceDemo = () => {
    // 模擬績效數據
    const [performanceData, setPerformanceData] = useState({
        overallScore: 85,
        kpiAchievement: [
            {
                id: 1,
                name: '專案完成率',
                target: 100,
                actual: 95,
                weight: 0.3,
                status: 'excellent'
            },
            {
                id: 2,
                name: '客戶滿意度',
                target: 90,
                actual: 88,
                weight: 0.3,
                status: 'good'
            },
            {
                id: 3,
                name: '創新提案',
                target: 3,
                actual: 2,
                weight: 0.2,
                status: 'needs-improvement'
            },
            {
                id: 4,
                name: '團隊協作',
                target: 100,
                actual: 92,
                weight: 0.2,
                status: 'good'
            }
        ],
        performanceLevels: [
            { level: 'A', range: '90-100', bonus: '120%' },
            { level: 'B', range: '80-89', bonus: '100%' },
            { level: 'C', range: '70-79', bonus: '80%' },
            { level: 'D', range: '60-69', bonus: '60%' },
            { level: 'E', range: '0-59', bonus: '0%' }
        ]
    });

    // 績效狀態圖示
    const getStatusIcon = (status) => {
        switch (status) {
            case 'excellent':
                return <CheckCircle className="text-green-500" />;
            case 'good':
                return <TrendingUp className="text-blue-500" />;
            case 'needs-improvement':
                return <XCircle className="text-red-500" />;
            default:
                return null;
        }
    };

    return (
        <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
                {/* 左側：KPI達成情況 */}
                <div>
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <Award className="mr-2 text-primary-600" />
                        王小明 - 績效評估報告
                    </h2>

                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <Target className="mr-2 text-primary-600" />
                                <span>總體績效分數</span>
                            </div>
                            <span className="text-2xl font-bold text-primary-600">
                                {performanceData.overallScore}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                                className="bg-primary-600 h-2.5 rounded-full"
                                style={{ width: `${performanceData.overallScore}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold flex items-center">
                            <Edit className="mr-2 text-primary-600" />
                            KPI指標達成情況
                        </h3>

                        {performanceData.kpiAchievement.map((kpi) => (
                            <div
                                key={kpi.id}
                                className="bg-white p-4 rounded-lg shadow-sm flex items-center"
                            >
                                <div className="mr-4">
                                    {getStatusIcon(kpi.status)}
                                </div>
                                <div className="flex-grow">
                                    <div className="flex justify-between mb-2">
                                        <span>{kpi.name}</span>
                                        <span className="text-sm text-gray-600">
                                            {kpi.actual} / {kpi.target}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className={`h-2.5 rounded-full ${kpi.status === 'excellent'
                                                    ? 'bg-green-500'
                                                    : kpi.status === 'good'
                                                        ? 'bg-blue-500'
                                                        : 'bg-red-500'
                                                }`}
                                            style={{
                                                width: `${(kpi.actual / kpi.target * 100).toFixed(2)}%`
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 右側：績效等級與獎金 */}
                <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <User className="mr-2 text-primary-600" />
                        績效等級與獎金對照表
                    </h3>

                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="p-3 text-left">績效等級</th>
                                    <th className="p-3 text-center">分數範圍</th>
                                    <th className="p-3 text-right">獎金係數</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performanceData.performanceLevels.map((level) => (
                                    <tr
                                        key={level.level}
                                        className={`border-b ${performanceData.overallScore >= parseInt(level.range.split('-')[0]) &&
                                                performanceData.overallScore <= parseInt(level.range.split('-')[1])
                                                ? 'bg-primary-50 font-bold'
                                                : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded ${performanceData.overallScore >= parseInt(level.range.split('-')[0]) &&
                                                    performanceData.overallScore <= parseInt(level.range.split('-')[1])
                                                    ? 'bg-primary-600 text-white'
                                                    : 'bg-gray-200 text-gray-700'
                                                }`}>
                                                {level.level}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">{level.range}</td>
                                        <td className="p-3 text-right">{level.bonus}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center">
                            <Award className="mr-2 text-yellow-500" />
                            績效評估說明
                        </h4>
                        <p className="text-sm text-gray-600">
                            績效評估每季度進行一次，綜合考慮工作產出、團隊協作、創新能力等多個維度。
                            本報告將作為年度績效考核、薪資調整和職業發展規劃的重要依據。
                        </p>
                    </div>
                </div>
            </div>

            {/* 底部操作區域 */}
            <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-md">
                <div>
                    <h4 className="font-semibold">績效報告與發展建議</h4>
                    <p className="text-sm text-gray-600">
                        您可以查看詳細的績效報告，並與主管討論職業發展計劃
                    </p>
                </div>
                <div className="flex space-x-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                        查看詳細報告
                    </button>
                    <button className="border border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors">
                        預約主管溝通
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PerformanceDemo;